/**
 * One build serves every environment: the pane is hosted at office.<domain>
 * and the API origin is derived from the page hostname at runtime
 * (office.staging-filingramp.com -> api.staging-filingramp.com). In dev the
 * origin stays empty so the relative /v1 paths hit the Vite proxy.
 */
const isDev = import.meta.env.DEV
const { hostname, protocol } = window.location

export const apiOrigin = isDev ? '' : `${protocol}//${hostname.replace(/^office\./, 'api.')}`

/** FilingRamp API base (login, account lookups). */
export const apiBaseUrl = `${apiOrigin}/v1`
/** ramp-ai-service base (REST + agent socket). */
export const aiApiBaseUrl = `${apiOrigin}/v1/ai`
export const aiAgentSocketPath = '/v1/ai-agent'
/**
 * V2 search-service base (full-text/faceted search). The checklist-item picker
 * in the scan pane lists items via POST {searchApiBaseUrl}/checklist-items —
 * mirrors ramp-web-app's `searchChecklistItems` (REACT_APP_V2_API_ENDPOINT).
 * In dev the origin stays empty so the relative /v2 path hits the Vite proxy.
 */
export const searchApiBaseUrl = `${apiOrigin}/v2/search`

/**
 * ramp-web-app origin (office.<domain> -> app.<domain>), for deep links back to
 * the web UI. In dev it falls back to the staging client (matching sso.ts), or
 * VITE_CLIENT_URL when set.
 */
export const webAppOrigin = isDev ? 'http://localhost' : `${protocol}//${hostname.replace(/^office\./, 'app.')}`

/** Web-app page for a checklist item, opened from a scan result. */
export const checklistItemUrl = (id: string): string => `${webAppOrigin}/checklist-items/${id}`

/**
 * Document mirror websocket (y-websocket endpoint on ramp-ai-service). In dev
 * it targets the Vite dev server, whose /v1 proxy forwards ws upgrades.
 */
export const docSyncSocketUrl = (() => {
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:'
  const wsHost = isDev ? window.location.host : hostname.replace(/^office\./, 'api.')
  return `${wsProtocol}//${wsHost}/v1/ai/doc-sync`
})()
